import { useState, useEffect, useRef } from 'react';

// A plain <input type="date"> is only typable on desktop browsers — on
// mobile Safari/Chrome it forces a wheel/scroll picker with no way to type
// a date directly. This renders a typable DD/MM/YYYY text field (works
// identically everywhere) plus a calendar icon that opens the native
// picker for anyone who still wants to click-and-select, so both ways of
// entering a date work on every device.
function isoToDisplay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
function displayToIso(display) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display || '');
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = parseInt(dd, 10), mo = parseInt(mm, 10), y = parseInt(yyyy, 10);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return `${yyyy}-${mm}-${dd}`;
}

export default function DateInput({ value, onChange, required, placeholder, className = 'form-control', min, max }) {
  const [text, setText] = useState(isoToDisplay(value));
  const hiddenRef = useRef(null);

  // Keep the typed display in sync when the value changes from elsewhere
  // (e.g. the native picker, or the form resetting).
  useEffect(() => { setText(isoToDisplay(value)); }, [value]);

  function handleTextChange(e) {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(formatted);
    if (formatted === '') { onChange(''); return; }
    const iso = displayToIso(formatted);
    if (iso) onChange(iso);
  }

  function openPicker() {
    const el = hiddenRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch (e) { /* fall through to click */ }
    }
    el.focus();
    el.click();
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={className}
        style={{ paddingRight: 34 }}
        value={text}
        onChange={handleTextChange}
        placeholder={placeholder || 'DD/MM/YYYY'}
        required={required}
      />
      <i
        className="fas fa-calendar-alt"
        onClick={openPicker}
        title="Pick a date"
        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-secondary, #64748b)', fontSize: 14 }}
      ></i>
      <input
        ref={hiddenRef}
        type="date"
        value={value || ''}
        min={min}
        max={max}
        onChange={e => onChange(e.target.value)}
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'absolute', opacity: 0, width: 1, height: 1, border: 0, padding: 0, pointerEvents: 'none' }}
      />
    </div>
  );
}
